/**
 * SubtitleManagementDialog - Modal dialog for managing subtitles on a video asset
 *
 * Wraps SubtitleManager in a dialog for use in VideoAssetsRow.
 * Provides a dedicated subtitle management experience separate from video details.
 */

import { X } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { SubtitleManager } from "./SubtitleManager";
import type { ExistingSubtitle } from "./SubtitleRow";

interface SubtitleManagementDialogProps {
  /** Whether the dialog is open */
  readonly open: boolean;
  /** Callback when dialog should close */
  readonly onClose: () => void;
  /** Title name for display */
  readonly titleName: string;
  /** Asset kind (Feature or Trailer) */
  readonly assetKind: "feature" | "trailer";
  /** Asset ID for API operations */
  readonly assetId: string;
  /** Existing subtitles for the asset */
  readonly subtitles: ExistingSubtitle[];
  /** Callback to refresh data after changes */
  readonly onRefresh: () => Promise<void>;
}

/**
 * Modal dialog for managing subtitles on a video asset
 */
export function SubtitleManagementDialog({
  open,
  onClose,
  titleName,
  assetKind,
  assetId,
  subtitles,
  onRefresh,
}: Readonly<SubtitleManagementDialogProps>): ReactElement | null {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
    }
  }, [open]);

  if (!open && !isClosing) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const isVisible = open && !isClosing;
  const kindLabel = assetKind === "feature" ? "Feature" : "Trailer";

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/60 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-800/20 backdrop-blur-md border border-neutral-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-800">
          <div>
            <h2 className="text-xl font-semibold text-white">{kindLabel} Subtitles</h2>
            <p className="text-sm text-neutral-400 mt-1">{titleName}</p>
          </div>
          <button type="button" onClick={handleClose} className="text-neutral-400 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
          {subtitles.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-neutral-500 text-sm mb-4">No subtitles uploaded yet</p>
            </div>
          ) : null}

          <SubtitleManager
            mode="admin"
            assetId={assetId}
            assetKind={assetKind}
            existingSubtitles={subtitles}
            onRefresh={onRefresh}
            compact={false}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-6 border-t border-neutral-800">
          <div className="text-xs text-neutral-500">
            {subtitles.length} subtitle{subtitles.length === 1 ? "" : "s"} • Click a subtitle row to view validation
            details
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm text-neutral-300 hover:text-white border border-neutral-700 hover:border-neutral-600 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
